import { ChangeDetectionStrategy, Component, computed } from '@angular/core';

import { ReadonlyDisplayBase } from './readonly-display.base';
import { identityDocumentDisplayParts } from '../../composite-subfields.util';

/**
 * Read display for an `identity-document` answer: the document type as the same neutral chip the
 * edit field uses (`.dd-doc-type-chip`, _chips.scss), followed by the masked reminder with its
 * bullets muted and the visible tail in tabular figures — `[ID-kaart] ••••1234`. The value is the
 * whole composite object `{ type, number }`; `number` is already the masked reminder, never the
 * secret.
 *
 * `identityDocumentDisplayParts` (`composite-subfields.util.ts`) is the ONE place the composition
 * rule lives — the list cell and the review formatter print the same parts as one string through
 * `identityDocumentDisplayText`, so a half-filled value never reads differently per surface:
 * type only → the chip alone; number only → the masked text alone; neither → '—'.
 *
 * Presentational: both inputs come from the row; the type label lives INSIDE this field's own
 * value, so there is no option-label mapping or cross-field lookup to keep here.
 */
@Component({
  selector: 'dd-identity-document-display',
  changeDetection: ChangeDetectionStrategy.OnPush,
  // display: contents — same reason as ValueDisplayComponent: no box inside the value span.
  styles: `
    :host {
      display: contents;
    }

    .dd-identity-document-display__masked {
      font-variant-numeric: tabular-nums;
    }

    .dd-identity-document-display__bullets {
      color: var(--dd-field-fg-disabled, #a9a9ad);
    }
  `,
  template: `@if (parts().type; as type) {<span class="dd-doc-type-chip">{{ type }}</span>}@if (parts().type && parts().masked) {&ngsp;}@if (parts().masked) {<span class="dd-identity-document-display__masked"><span class="dd-identity-document-display__bullets">{{ bullets() }}</span>{{ tail() }}</span>}@if (!parts().type && !parts().masked) {—}`,
})
export class IdentityDocumentDisplayComponent extends ReadonlyDisplayBase {
  readonly parts = computed(() => identityDocumentDisplayParts(this.value()));
  /** The leading bullet run of the masked reminder ('••••' of '••••1234'); the whole string when it is bullets only. */
  readonly bullets = computed(() => this.parts().masked?.match(/^•+/)?.[0] ?? '');
  readonly tail = computed(() => this.parts().masked?.slice(this.bullets().length) ?? '');
}
